import SelectComponent from 'formiojs/components/select/Select';
import { getKey, checkConditionalityAcrossForms, shouldEditSelectUseOriginalConditionality, unset } from '../extended-field-helper';
import { KeyCode } from '../../../../enums';
import { FormioConditionalityUtils } from '../../../../utils/formio-conditionality-utils';
import { shouldUpdateOriginalLabel, updateOriginalLabel } from '../../../../utils/data-field-metadata/data-field-metadata-utils';
import _ from 'lodash';

export class EditableSelect extends SelectComponent {
	static schema(...extend: any) {
		return SelectComponent.schema(
			{
				dataSrc: 'values',
				template: '<span>{{ item.label }}</span>',
				lazyLoad: false,
				searchEnabled: false,
				dataFieldId: null,
				disableAddingRemovingRows: true
			},
			...extend
		);
	}

	// @ts-ignore
	set visible(value: boolean) {
		var isChanged = this.visible !== value;

		super.visible = value;

		if (isChanged) {
			this.setValue(this.dataValue);
		}
	}

	get visible() {
		return super.visible;
	}

	// @ts-ignore
	get key(): string {
		return getKey(this, super.key);
	}

	/**
	 * Check if this component is conditionally visible.
	 *
	 * @param data
	 * @return {boolean}
	 */
	conditionallyVisible(data: any): boolean {
		if (shouldEditSelectUseOriginalConditionality(this)) {
			return super.conditionallyVisible(data);
		}

		return checkConditionalityAcrossForms(this);
	}

	checkCondition(row: any, data: any) {
		return FormioConditionalityUtils.checkCondition(
			this.component,
			row || this.data,
			data || this.rootValue,
			this.root ? (this.root as any)._form : {},
			this
		);
	}

	unset(): void {
		unset(this);
	}

	updateValue(...args: any[]): void {
		super.updateValue(...args);
		this.createChoicesAnnouncement();
	}

	createChoicesAnnouncement() {
		if (this.choices && this.choices.containerInner && this.choices.containerInner.element) {
			const value = this.getValue();
			const valueLabel = this.getValueLabel(value);
			// Remove html tags from label for screen reader announcement
			const cleanedLabel = valueLabel ? valueLabel.toString().replace(/<[^>]*>/g, '') : '';

			const existingAnnouncement = this.choices.containerInner.element.querySelector('.sr-only-selection');
			if (existingAnnouncement) {
				existingAnnouncement.remove();
			}

			const srAnnouncement = document.createElement('span');
			srAnnouncement.classList.add('sr-only', 'sr-only-selection');
			srAnnouncement.setAttribute('aria-live', 'polite');
			srAnnouncement.style.position = 'absolute';
			srAnnouncement.style.width = '1px';
			srAnnouncement.style.height = '1px';
			srAnnouncement.style.padding = '0';
			srAnnouncement.style.margin = '-1px';
			srAnnouncement.style.overflow = 'hidden';
			srAnnouncement.style.clip = 'rect(0, 0, 0, 0)';
			srAnnouncement.style.whiteSpace = 'nowrap';
			srAnnouncement.style.border = '0';
			srAnnouncement.textContent = cleanedLabel ? `Selected: ${cleanedLabel}` : 'No selection';

			this.choices.containerInner.element.appendChild(srAnnouncement);

			const choiceList = this.choices.containerInner.element.querySelector('.choices__list');
			if (choiceList) {
				choiceList.setAttribute(
					'aria-activedescendant',
					value ? `choice-${this.id}-${this.selectOptions.findIndex((o: { value: any }) => o.value === value)}` : ''
				);
			}
		}
	}

	getValueLabel(value: any): string {
		if (_.isNil(value)) return '';

		const option = this.selectOptions.find((o: { value: any }) => _.isEqual(o.value, value));
		return option ? option.label : value;
	}

	private choicesTextInputOnKeyDown = (e: any) => {
		if (e.keyCode === KeyCode.Enter) {
			// Set data to new value and add to list
			this.dataValue = e.target.value;
			this.addValueOptions();
			this.setChoicesValue(this.dataValue);

			this.closeDropdown();
		}
	};

	private closeDropdownOnEnterPressed = (e: any) => {
		if (e.keyCode === KeyCode.Enter) {
			if (this.choices.dropdown.isActive) {
				this.closeDropdown();
			}
		}
	};

	private closeDropdown() {
		this.choices.clearInput();
		this.choices.hideDropdown(true);
		this.choices.containerOuter.focus();
	}

	async attach(element: HTMLElement) {
		const superAttachResult = (await super.attach(element)) ?? Promise.resolve();

		// This does not use the existing helper to do oddities in the refs provided by the select component
		if (this.component?.dataFieldId == null || (typeof this.component.dataFieldId === 'string' && this.component.dataFieldId.trim() === '')) {
			return;
		}

		let event = `${this.component.dataFieldId}.${this.inputInfo.changeEvent}`;

		for (const selectContainer of this.refs.selectContainers || []) {
			this.removeEventListener(selectContainer, this.inputInfo.changeEvent);
		}

		this.addEventListener(this.refs.selectContainer as any, this.inputInfo.changeEvent, (e: Event) => {
			const x = (e?.target as HTMLInputElement)?.value;

			this.emit(event, { value: x, id: this.id, dataId: this.root.data.id });
		});

		if (this.component.allowCustomValues && this.choices?.input?.element) {
			this.choices.input.element.removeEventListener('keydown', this.choicesTextInputOnKeyDown);
			this.addEventListener(this.choices.input.element, 'keydown', this.choicesTextInputOnKeyDown);
		} else if (this.element) {
			this.element.removeEventListener('keydown', this.closeDropdownOnEnterPressed);
			this.addEventListener(this.element, 'keydown', this.closeDropdownOnEnterPressed);
		}

		this.on(
			event,
			({ value, id, dataId }: any) => {
				if (id !== this.id && dataId === this.root.data.id) {
					// use updateValue if need other events to fired
					// however, we want to bypass events and just set the value
					this.setValue(value);
				}
			},
			true
		);

		if (shouldUpdateOriginalLabel(this)) {
			await updateOriginalLabel(this);
		}

		this.setAriaAttributes();

		return superAttachResult;
	}

	private setAriaAttributes() {
		if (this.choices?.containerInner?.element != null && this.component?.label) {
			// Add additional ARIA attributes for accessibility
			this.choices.containerInner.element.setAttribute('aria-label', this.component.label);
			this.choices.containerInner.element.setAttribute('role', 'combobox');
			this.choices.containerInner.element.setAttribute('aria-expanded', 'false');
			this.choices.containerInner.element.setAttribute('aria-haspopup', 'listbox');

			const choiceList = this.choices.containerInner.element.querySelector('.choices__list');
			if (choiceList) {
				choiceList.setAttribute('role', 'listbox');

				// Add IDs to each option for accessibility
				const options = choiceList.querySelectorAll('.choices__item');
				options.forEach((option: any, index: number) => {
					option.setAttribute('role', 'option');
					option.id = `choice-${this.id}-${index}`;
					option.setAttribute('aria-selected', 'false');
				});
			}

			// Add listeners for dropdown events to update ARIA states
			this.choices.passedElement.element.removeEventListener('showDropdown', this.onShowDropdown);
			this.choices.passedElement.element.removeEventListener('hideDropdown', this.onHideDropdown);

			this.choices.passedElement.element.addEventListener('showDropdown', this.onShowDropdown);
			this.choices.passedElement.element.addEventListener('hideDropdown', this.onHideDropdown);
		}
	}

	private onShowDropdown = () => {
		if (this.choices.containerInner && this.choices.containerInner.element) {
			this.choices.containerInner.element.setAttribute('aria-expanded', 'true');
		}
	};
	private onHideDropdown = () => {
		if (this.choices.containerInner && this.choices.containerInner.element) {
			this.choices.containerInner.element.setAttribute('aria-expanded', 'false');
		}
	};
}
